const DOCUMENT_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/csv",
  "text/plain",
]);

const DOCUMENT_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "ppt",
  "pptx",
  "txt",
]);

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp"]);
const VIDEO_EXTENSIONS = new Set(["mp4", "avi", "mov", "webm"]);
const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "ogg", "m4a", "webm"]);

function getFileExtension(fileName) {
  if (!fileName || typeof fileName !== "string") {
    return "";
  }
  const parts = fileName.split(".");
  if (parts.length < 2) {
    return "";
  }
  return parts.pop().toLowerCase();
}

/**
 * Classify a chat attachment for message_type routing.
 * @returns {"image" | "gif" | "video" | "audio" | "document" | "other"}
 */
export function classifyChatFile(file) {
  const mime = (file?.type || "").toLowerCase();
  const ext = getFileExtension(file?.name);

  if (mime === "image/gif" || ext === "gif") {
    return "gif";
  }
  if (mime.startsWith("image/") || IMAGE_EXTENSIONS.has(ext)) {
    return "image";
  }
  if (mime.startsWith("video/") || VIDEO_EXTENSIONS.has(ext)) {
    return "video";
  }
  if (mime.startsWith("audio/") || AUDIO_EXTENSIONS.has(ext)) {
    return "audio";
  }
  if (DOCUMENT_MIME_TYPES.has(mime) || DOCUMENT_EXTENSIONS.has(ext)) {
    return "document";
  }

  return "other";
}

/**
 * Full-color send class when the composer has text or any queued attachment.
 * Images/videos live in `mediaCollection`; PDFs, docs, and other files live in `files`.
 */
export function getChatSendButtonClass({
  message,
  files = [],
  mediaCollection = [],
} = {}) {
  const hasText = Boolean(message && String(message).length > 0);
  const hasFiles = Array.isArray(files) && files.length > 0;
  const hasMedia = Array.isArray(mediaCollection) && mediaCollection.length > 0;

  return hasText || hasFiles || hasMedia ? "chatSend isInput" : "chatSend";
}

/**
 * PDF and Word documents use the thumbnail upload pipeline.
 */
export function shouldUsePdfThumbnailUpload(file) {
  const mime = (file?.type || "").toLowerCase();
  const ext = getFileExtension(file?.name);

  return (
    mime === "application/pdf" ||
    mime === "application/msword" ||
    mime ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    ext === "pdf" ||
    ext === "doc" ||
    ext === "docx"
  );
}

/**
 * Personalize group name for the current viewer: replace their display name with "You".
 * Whole-token match only; used only for display (e.g. chat header). Not persisted.
 *
 * @param {string} groupName - Stored group title (e.g. "John, Sarah, Mike")
 * @param {string} receiverDisplayName - Current user's display name
 * @returns {string} Personalized title (e.g. "You, John, Mike") or unchanged groupName if no match
 */
export function personalizeGroupNameForReceiver(
  groupName,
  receiverDisplayName,
) {
  if (
    groupName == null ||
    (typeof groupName === "string" && groupName.trim() === "")
  ) {
    return typeof groupName === "string" ? groupName : "";
  }
  if (receiverDisplayName == null || typeof receiverDisplayName !== "string") {
    return groupName;
  }
  const receiverTrimmed = receiverDisplayName.trim();
  if (receiverTrimmed === "") {
    return groupName;
  }
  // Tokenize: split only on comma/semicolon so multi-word names (e.g. "Shubham Somani") stay one token
  const tokens = groupName
    .split(/\s*[,;]\s*/)
    .map((t) => t.trim())
    .filter((t) => t !== "");
  const remainingTokens = tokens.filter((t) => t !== receiverTrimmed);
  // Only add "You" if the receiver's name was actually in the group name
  if (remainingTokens.length === tokens.length) {
    return groupName;
  }
  if (remainingTokens.length === 0) {
    return "You";
  }
  return "You, " + remainingTokens.join(", ");
}

/**
 * True when the user is known to no longer belong to this salon (removed / reassigned).
 * Missing profile or missing salon_ids is treated as NOT removed — Firestore chat profiles
 * often omit salon_ids, and we should not zero out member counts in that case.
 */
export function isUserDeletedBySalon(user, salonId) {
  if (!user) {
    return false;
  }
  if (
    !user.salon_ids ||
    !Array.isArray(user.salon_ids) ||
    user.salon_ids.length === 0
  ) {
    return false;
  }
  if (salonId == null || salonId === "") {
    return false;
  }
  const target = Number(salonId);
  if (Number.isNaN(target)) {
    return false;
  }
  return !user.salon_ids.some((id) => Number(id) === target);
}

export function normalizeMemberIdKey(id) {
  if (id == null || id === "") {
    return null;
  }
  const asNumber = Number(id);
  if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
    return String(asNumber);
  }
  return String(id);
}

function uniqueMemberIds(ids) {
  const seen = new Set();
  const unique = [];
  for (const id of ids) {
    const key = normalizeMemberIdKey(id);
    if (key == null || seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(id);
  }
  return unique;
}

/**
 * Normalize GetMembers / GetChatUsers payloads into a flat user array.
 */
export function coerceMemberList(salonMembers) {
  if (Array.isArray(salonMembers)) {
    return salonMembers;
  }
  if (Array.isArray(salonMembers?.data)) {
    return salonMembers.data;
  }
  if (Array.isArray(salonMembers?.users)) {
    return salonMembers.users;
  }
  if (Array.isArray(salonMembers?.users?.data)) {
    return salonMembers.users.data;
  }
  return [];
}

function resolveViewerId(currentUserId) {
  if (currentUserId != null && currentUserId !== "") {
    return currentUserId;
  }
  try {
    const raw =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("SALON_SYMPHONY_USER")
        : null;
    if (!raw) {
      return null;
    }
    return JSON.parse(raw)?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Unique live people in the group header: resolved profiles + the viewer.
 * `receiver_profile` omits the logged-in user; do not use the team API list,
 * which NewChat also strips of the current user.
 */
export function getActiveMemberCount(
  selectedUser,
  currentUserId,
  salonId,
  _salonMembers,
) {
  const profiles = Array.isArray(selectedUser?.receiver_profile)
    ? selectedUser.receiver_profile
    : [];
  const counted = new Set();

  profiles.forEach((profile) => {
    if (!profile || profile.is_deleted === true) {
      return;
    }
    if (isUserDeletedBySalon(profile, salonId)) {
      return;
    }
    const key = normalizeMemberIdKey(profile.id);
    if (key) {
      counted.add(key);
    }
  });

  const hasUsersIds =
    Array.isArray(selectedUser?.users_ids) &&
    selectedUser.users_ids.length > 0;
  if (profiles.length === 0 && !hasUsersIds) {
    return 0;
  }

  const viewerKey = normalizeMemberIdKey(resolveViewerId(currentUserId));
  if (viewerKey) {
    counted.add(viewerKey);
  }

  if (counted.size === 0 && hasUsersIds) {
    return uniqueMemberIds(selectedUser.users_ids).length;
  }

  return counted.size;
}
